import { useState, useRef } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useAuth } from "@/core/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/shared/hooks/use-toast";
import { Button } from "@/shared/ui/button";
import { Input } from "@/shared/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "@/shared/ui/card";
import { 
  Upload, 
  FileText, 
  Image, 
  File, 
  Trash2, 
  Download, 
  Loader2,
  Paperclip
} from "lucide-react";
import {
  fetchAttachmentsByClaim,
  createAttachmentForClaim,
  deleteAttachmentRecord,
  uploadAttachmentFile,
  deleteAttachmentFile,
  getAttachmentPublicUrl,
  Attachment,
} from "../api/attachments";
import { createSupplierPortalClient } from "../api/supplier-portal";

interface FileAttachmentsProps {
  claimId: string;
  authorType?: "factory" | "supplier";
  supplierToken?: string;
}

export function FileAttachments({ claimId, authorType = "factory", supplierToken }: FileAttachmentsProps) {
  const { user } = useAuth();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [label, setLabel] = useState("");
  const supplierClient = supplierToken ? createSupplierPortalClient(supplierToken) : null;

  const { data: attachments, isLoading } = useQuery({
    queryKey: ["mod_8d_attachments", claimId],
    queryFn: () => supplierClient?.fetchAttachments() ?? fetchAttachmentsByClaim(claimId),
  });

  const deleteMutation = useMutation({
    mutationFn: async (attachment: { id: string; file_path: string }) => {
      // Delete from storage first
      try {
        await deleteAttachmentFile(attachment.file_path);
      } catch (error) {
        console.error("Storage delete error:", error);
      }

      // Delete from database
      if (supplierClient) {
        await supplierClient.deleteAttachment(attachment.id);
      } else {
        await deleteAttachmentRecord(attachment.id);
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["mod_8d_attachments", claimId] });
      toast({ title: "File deleted" });
    },
    onError: () => {
      toast({ title: "Failed to delete file", variant: "destructive" });
    },
  });

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Max 50MB
    if (file.size > 50 * 1024 * 1024) {
      toast({ title: "File too large (max 50MB)", variant: "destructive" });
      return;
    }

    setIsUploading(true);
    try {
      const fileExt = file.name.split(".").pop();
      let filePath: string;

      if (supplierClient) {
        const uploadTarget = await supplierClient.createAttachmentUploadUrl(file.name);
        filePath = uploadTarget.path;

        const { error: uploadError } = await supabase.storage
          .from("8d-attachments")
          .uploadToSignedUrl(filePath, uploadTarget.token, file);

        if (uploadError) throw uploadError;
      } else {
        filePath = `${claimId}/${Date.now()}-${Math.random().toString(36).substring(7)}.${fileExt}`;

        // Upload to storage
        await uploadAttachmentFile(filePath, file);
      }

      // Save to database
      const payload = {
        file_name: file.name,
        file_path: filePath,
        file_size: file.size,
        file_type: file.type,
        label: label.trim() || null,
        uploaded_by: user?.id ?? null,
        uploaded_by_type: authorType,
      };

      if (supplierClient) {
        await supplierClient.createAttachment(payload);
      } else {
        await createAttachmentForClaim(claimId, payload);
      }

      queryClient.invalidateQueries({ queryKey: ["mod_8d_attachments", claimId] });
      toast({ title: "File uploaded" });
      setLabel("");
    } catch (error) {
      console.error("Upload error:", error);
      toast({ title: "Failed to upload file", variant: "destructive" });
    } finally {
      setIsUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const getFileIcon = (fileType: string | null) => {
    if (!fileType) return <File className="h-5 w-5" />;
    if (fileType.startsWith("image/")) return <Image className="h-5 w-5 text-chart-2" />;
    if (fileType.includes("pdf")) return <FileText className="h-5 w-5 text-chart-3" />;
    return <File className="h-5 w-5 text-muted-foreground" />;
  };

  const formatFileSize = (bytes: number | null) => {
    if (!bytes) return "";
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2 text-base">
          <Paperclip className="h-4 w-4" />
          Attachments
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Upload Section */}
        <div className="space-y-2">
          <Input
            placeholder="Label (optional)"
            value={label}
            onChange={(e) => setLabel(e.target.value)}
            className="text-sm"
          />
          <div className="flex gap-2">
            <input
              ref={fileInputRef}
              type="file"
              className="hidden"
              onChange={handleFileUpload}
            />
            <Button
              variant="outline"
              size="sm"
              className="flex-1"
              onClick={() => fileInputRef.current?.click()}
              disabled={isUploading}
            >
              {isUploading ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Upload className="h-4 w-4 mr-2" />
              )}
              {isUploading ? "Uploading..." : "Upload File"}
            </Button>
          </div>
        </div>

        {/* Files List */}
        {isLoading ? (
          <div className="text-sm text-muted-foreground">Loading...</div>
        ) : attachments && attachments.length > 0 ? (
          <div className="space-y-2">
            {attachments.map((attachment) => (
              <div
                key={attachment.id}
                className="flex items-center gap-3 p-3 bg-secondary/50 rounded-lg border border-border"
              >
                {getFileIcon(attachment.file_type)}
                <div className="flex-1 min-w-0">
                  <div className="font-medium text-sm truncate">
                    {attachment.label || attachment.file_name}
                  </div>
                  {attachment.label && (
                    <div className="text-xs text-muted-foreground truncate">
                      {attachment.file_name}
                    </div>
                  )}
                  <div className="text-xs text-muted-foreground">
                    {formatFileSize(attachment.file_size)}
                    {attachment.uploaded_by_type === "supplier" && (
                      <span className="ml-2 text-chart-3">• Supplier</span>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-1">
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8"
                    onClick={() => window.open(getAttachmentPublicUrl(attachment.file_path), "_blank")}
                  >
                    <Download className="h-4 w-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8 text-destructive hover:text-destructive"
                    onClick={() => deleteMutation.mutate({ id: attachment.id, file_path: attachment.file_path })}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-sm text-muted-foreground text-center py-4">
            No attachments yet
          </div>
        )}
      </CardContent>
    </Card>
  );
}
